import React, { useEffect, useMemo, useState } from 'react'
import { XCircleIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { estoqueService } from '../../services/estoque'
import type {
  EstoqueItem,
  EstoqueLocal,
  EstoqueMovimentacao,
  EstoqueMovementType,
} from '../../types/estoque'
import EstoquePagination from './EstoquePagination'
import {
  PAGE_SIZE,
  SELECT_CLASS,
  formatQuantity,
  movementTypeBadgeClass,
  movementTypeLabel,
  paginateRows,
  sortRows,
} from './estoqueUi'

type Props = {
  items: EstoqueItem[]
  locais: EstoqueLocal[]
}

const EstoqueHistoricoTab: React.FC<Props> = ({ items, locais }) => {
  const [movements, setMovements] = useState<EstoqueMovimentacao[]>([])
  const [loading, setLoading] = useState(true)
  const [itemId, setItemId] = useState('')
  const [localId, setLocalId] = useState('')
  const [movementType, setMovementType] = useState<EstoqueMovementType | 'all'>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortKey, setSortKey] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const data = await estoqueService.listMovimentacoes({
          itemId: itemId || undefined,
          locationId: localId || undefined,
          movementType,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        })
        if (!cancelled) setMovements(data)
      } catch (err) {
        console.error(err)
        if (!cancelled) setMovements([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [itemId, localId, movementType, dateFrom, dateTo])
  const sorted = useMemo(
    () =>
      sortRows(movements, sortKey, sortOrder, (row, key) => {
        if (key === 'created_at') return row.created_at
        if (key === 'item') return row.item?.name
        if (key === 'local') return row.location?.name
        if (key === 'type') return row.movement_type
        if (key === 'quantity') return Number(row.quantity)
        if (key === 'user') return row.created_by_user?.name
        return ''
      }),
    [movements, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page)
  const hasActiveFilters = Boolean(
    itemId || localId || movementType !== 'all' || dateFrom || dateTo
  )
  const clearFilters = () => {
    setItemId('')
    setLocalId('')
    setMovementType('all')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }
  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder(key === 'created_at' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  return (
    <div>
      <div className="mb-4">
        <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
          Histórico
        </Heading>
        <Text size="sm" color="muted" className="mt-1">
          Filtre e consulte entradas, saídas e ajustes registrados no estoque.
        </Text>
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Item</label>
          <select
            value={itemId}
            onChange={(e) => {
              setItemId(e.target.value)
              setPage(1)
            }}
            className={`min-w-[180px] ${SELECT_CLASS}`}
          >
            <option value="">Todos</option>
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Local</label>
          <select
            value={localId}
            onChange={(e) => {
              setLocalId(e.target.value)
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todos</option>
            {locais.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Tipo</label>
          <select
            value={movementType}
            onChange={(e) => {
              setMovementType(e.target.value as EstoqueMovementType | 'all')
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="all">Todos</option>
            <option value="in">Entrada</option>
            <option value="out">Saída</option>
            <option value="transfer">Transferência</option>
            <option value="adjustment">Ajuste</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">De</label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Até</label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center gap-1 whitespace-nowrap"
          >
            <XCircleIcon className="h-4 w-4" />
            Limpar filtros
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <SortableTableHeader sortKey="created_at" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Data
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="item" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Item
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="local" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Local
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="type" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Tipo
                  </SortableTableHeader>
                  <SortableTableHeader sortKey="quantity" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3 text-right">
                    Qtd
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Antes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Depois</th>
                  <SortableTableHeader sortKey="user" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Usuário
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Descrição</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhuma movimentação encontrada.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {new Date(m.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{m.item?.name ?? m.item_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{m.location?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${movementTypeBadgeClass(m.movement_type)}`}>
                          {movementTypeLabel(m.movement_type, Number(m.quantity_before), Number(m.quantity_after))}
                        </span>
                        {m.movement_type === 'transfer' && m.related_location?.name && (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            {Number(m.quantity_after) < Number(m.quantity_before) ? 'Para' : 'De'}{' '}
                            {m.related_location.name}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">{formatQuantity(m.quantity)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatQuantity(m.quantity_before)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatQuantity(m.quantity_after)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{m.created_by_user?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{m.description}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <EstoquePagination page={page} total={sorted.length} onPageChange={setPage} pageSize={PAGE_SIZE} />
    </div>
  )
}

export default EstoqueHistoricoTab
